import BaseService from "./BaseService";
import { WRO, WroAllocationsRequest, WroLineAllocations, WroLineAllocationsRequest, WroLinkRequest } from "models/InventoryModel";
import { NetSuiteReceivingStatus } from "shared/utils/netsuiteStatus";

/** Where a short WRO's missing units went (sbly-dash-collector shortfallClassification.js) */
export type ShortfallCategory = "recovered" | "recovery_pending" | "mix_up" | "po_balanced" | "not_shipped" | "claimable" | "not_claimable";

export type ShortfallEvidence = {
  category: ShortfallCategory;
  units: number;
  recoveryWroId?: string;
  recoveryWroName?: string;
  siblingSku?: string;
  poNumbers?: string[];
  /** packing_list: the supplier's packing list shows these units were never shipped */
  reason?: "none_received" | "mostly_missing" | "packing_list";
  shipped?: number;
};

export type MissingUnitsLine = {
  inventoryId: string;
  sku: string | null;
  name: string | null;
  expected: number;
  received: number;
  missing: number;
  poNumbers: string[];
  unitPrice: number | null;
  /** The supplier's packing list, not a guess, decided what was shipped */
  packingListChecked: boolean;
  breakdown: Record<ShortfallCategory, number>;
  /** The automatic split, when a person's review replaced it */
  automatic?: Record<ShortfallCategory, number>;
  evidence: ShortfallEvidence[];
  review: { category: ShortfallCategory; note: string | null; reviewedAt: string } | null;
  claimableCredit: number | null;
};

export type LedgerStage = "waiting" | "to_claim" | "claimed" | "credited" | "denied" | "dismissed" | "nothing_to_claim";

export type LedgerClaim = {
  id: number;
  wroId: string;
  status: "sent" | "info_requested" | "credited" | "denied" | "dismissed";
  caseNumber: string | null;
  subject: string | null;
  claimedAmount: number | null;
  creditedAmount: number | null;
  note: string | null;
  claimedAt: string;
  lastActivityAt: string;
  resolvedAt: string | null;
  events: Array<{ id: number; event: string; status: string | null; amount: number | null; detail: string | null; at: string }>;
};

export type LedgerCredit = { transactionId: string; date: string; amount: number; caseNumber: string | null; reason: string | null };

export type SupplierDocumentType = "invoice" | "packing_list";

/** A supplier invoice or packing list read from the purchasing inbox */
export type SupplierDocumentRef = { id: number; poNumber: string; documentType: SupplierDocumentType; fileName: string; receivedAt: string };

export type ReconciliationFlag =
  | "billed_not_shipped" | "billed_over_order" | "billed_not_received" | "shipped_not_received"
  | "arrived_as_other_size" | "short_shipped" | "not_on_po";

export type ReconciliationLine = {
  inventoryId?: string;
  productNumber: string | null;
  name?: string | null;
  description?: string | null;
  sku?: string | null;
  ordered?: number;
  shipped: number | null;
  billed: number | null;
  received?: number;
  mixedUp?: number;
  notes?: string[];
  flags: Array<{ flag: ReconciliationFlag; units: number; value: number | null }>;
};

/** Per PO: were we billed only for what shipped, and did what shipped arrive (poReconciliation.js) */
export type PurchaseOrderCheck = {
  poNumber: string;
  invoice: { id: number; fileName: string; invoiceNumber: string | null; total: number | null; verified: boolean; receivedAt: string } | null;
  packingList: { id: number; fileName: string; invoiceNumber: string | null; receivedAt: string } | null;
  lines: ReconciliationLine[];
  extras: ReconciliationLine[];
  overbilled: number;
  billedNotReceived: number;
  shippedNotReceived: number;
  needsAttention: boolean;
};

/** An email the claims agent drafted, waiting for a person to review, edit and send (claimsOutbox.js) */
export type ClaimsOutboxEmail = {
  key: string;
  kind: "claim" | "document_request";
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  attachments: Array<{ id: number; fileName: string; poNumber: string; documentType: SupplierDocumentType }>;
  // claim
  wroId?: string;
  wroName?: string | null;
  completedAt?: string;
  units?: number;
  creditTotal?: number | null;
  // document_request
  requests?: Array<{ poNumber: string; missing: SupplierDocumentType[]; wroIds: string[]; attempt: number; firstRequestedAt: string | null }>;
};

export type MissingUnitsWro = {
  wroId: string;
  name: string;
  poNumbers: string[];
  /** The supplier documents a claim on this WRO attaches */
  documents: SupplierDocumentRef[];
  /** When the claims agent asked the supplier for each PO's documents, and whether it escalated to Slack */
  documentRequests: Array<{ poNumber: string; count: number; firstAt: string | null; lastAt: string | null; escalatedAt: string | null }>;
  completedAt: string | null;
  daysSinceCompleted: number | null;
  claimableFrom: string | null;
  missingUnits: number;
  claimableUnits: number;
  pendingUnits: number;
  claimableCredit: number | null;
  lines: MissingUnitsLine[];
  claims: LedgerClaim[];
  latestClaim: LedgerClaim | null;
  credits: LedgerCredit[];
  creditedAmount: number;
  valued: Record<ShortfallCategory, { units: number; value: number | null }>;
  stage: LedgerStage;
  creditedThenFound: boolean;
};

export type MissingUnitsLedger = {
  since: string | null;
  fileAfterDays: number;
  reviewableCategories: ShortfallCategory[];
  purchaseOrders: PurchaseOrderCheck[];
  summary: {
    wros: number;
    missingUnits: number;
    byCategory: Record<ShortfallCategory, { units: number; value: number; unpricedUnits: number }>;
    toClaim: { wros: number; value: number };
    waiting: number;
    claimed: number;
    credited: number;
    outstanding: { claims: number; value: number };
    denied: { claims: number; value: number };
    creditedThenFound: number;
  };
  wros: MissingUnitsWro[];
  otherCredits: Array<LedgerCredit & { wroId: string }>;
};

export type WroClaim = {
  id: number;
  status: "sent" | "info_requested" | "credited" | "denied" | "dismissed" | string;
  /** Amount originally requested */
  creditAmount: number | null;
  /** Amount actually received (set when credited) */
  creditedAmount: number | null;
  note: string | null;
  sentTo: string | null;
  subject: string | null;
  claimedAt: string;
  lastActivityAt: string;
  resolvedAt: string | null;
};

export type WroShortfallLine = {
  inventoryId: string;
  /** The PO line this share of the WRO line belongs to; null = the part no PO takes */
  poNumber: string | null;
  sku: string | null;
  name: string | null;
  expected: number;
  received: number;
  missing: number;
  unitPrice: number | null;
  credit: number | null;
};

export type WroShortfall = {
  wroId: string;
  /** The WRO's own PO string in ShipBob (may carry a suffix) */
  purchaseOrderNumber: string | null;
  /** Our matched PO numbers joined with ", " (a consolidated WRO can be short for several); null when none */
  poNumber: string | null;
  poNumbers: string[];
  lastUpdatedDate: string | null;
  claim: WroClaim | null;
  lines: WroShortfallLine[];
  missingUnits: number;
  /** Null when any line is missing a unit price */
  creditTotal: number | null;
};

export type NetSuitePoPreview = {
  poNumber: string;
  tranId: string;
  vendorId: string;
  /** Vendor name and billing currency as NetSuite has them (null when the vendor lookup found nothing) */
  vendorName: string | null;
  currency: string | null;
  tranDate: string;
  shipDate: string | null;
  shipTo: string;
  total: number | null;
  lines: Array<{
    inventoryId: string;
    sku: string | null;
    name: string | null;
    quantity: number;
    rate: number | null;
    amount: number | null;
    expectedReceiptDate: string | null;
    netsuiteItemId: string | null;
    netsuiteItemName: string | null;
    netsuiteItemInactive: boolean;
  }>;
  blockers: string[];
  existing: { netsuitePoId: string; tranId: string; pushedAt: string; url: string } | null;
  /** A NetSuite PO with this number that was not created from here */
  tranIdClash: { netsuitePoId: string; url: string } | null;
};

/** Per pushed PO: how the tool's lines compare to the NetSuite record (keyed by PO number). */
export type NetSuiteDriftLine = {
  inventoryId: string;
  sku: string | null;
  name: string | null;
  poQty: number | null;
  nsQty: number | null;
  poRate: number | null;
  nsRate: number | null;
};
export type NetSuiteDriftEntry = {
  netsuitePoId: string;
  tranId: string;
  url: string;
  /** NetSuite returned no record for the linked id */
  missing: boolean;
  lastModified: string | null;
  /** NetSuite's own status text, e.g. "Partially Received" */
  netsuiteStatus: string | null;
  /** What that status means for receiving; null for a code the server doesn't know */
  receivingStatus: NetSuiteReceivingStatus | null;
  differs: boolean;
  diffs: NetSuiteDriftLine[];
  /** The NetSuite number this PO's current number maps to */
  expectedTranId: string;
  /** NetSuite still carries another number (the PO was renamed in the tool) */
  nameDiffers: boolean;
};
export type NetSuiteDrift = Record<string, NetSuiteDriftEntry>;

/** Line items read off an uploaded PO PDF, each with SKU match suggestions */
export type PoPdfLine = {
  productNumber: string | null;
  inventoryId: string | null;
  description: string;
  quantity: number;
  unitPrice: number | null;
  suggestions: Array<{ itemId: number; label: string; score: number; matchedBy: string }>;
  decisive: boolean;
};
export type PoPdfExtraction = {
  poNumber: string | null;
  poDate: string | null;
  currency: string;
  lines: PoPdfLine[];
};

/** Latest confirmed quote price per freight method, keyed by ShipBob inventory id. */
export type CatalogPrices = Record<string, Partial<Record<"40hc" | "40gp" | "20dc" | "lcl" | "fob", number>>>;

/** PO-line suggestions from the SKU master: prices + Danican product numbers */
/** Tariff rates (fractions) a blank PO line can start from, keyed by inventory id or supplier id */
export type CatalogTariffs = {
  items: Record<string, number>;
  suppliers: Record<string, number>;
  recentByItem: Record<string, number>;
  recentBySupplier: Record<string, number>;
};

export type CatalogSuggestions = {
  prices: CatalogPrices;
  manufacturerSkus: Record<string, string>;
  tariffs: CatalogTariffs;
};

export type LeadTimesResponse = {
  manufacturerLeadTimes: Array<{ manufacturer: string; leadTimeMonths: number }>;
  inventoryLeadTimes: Array<{ inventoryId: string; leadTimeMonths: number }>;
  countryAdjustments: Array<{ country: string; adjustmentMonths: number }>;
  defaultLeadTimeMonths: number;
};

// The API answers an unexpected failure with a generic `error` and the real reason (e.g. what
// ShipBob rejected) at the start of `description`. Show the reason, not the generic line.
const GENERIC_SERVER_ERROR = "An Internal Server Error has Occurred.";
/** One product's demand adjustments, by month ("YYYY-MM") */
export interface ProductDemandSettings {
  /** Multiplier on the month's projected sales */
  salesModifiers: Record<string, number>;
  /** The month's projected sales in units, replacing the projection */
  manualOverrides: Record<string, number>;
}

/** Unsaved demand settings to forecast with; a field left out keeps what is saved */
export interface ForecastWhatIf extends Partial<ProductDemandSettings> {
  /** Daily sales rate in place of the projection basis */
  unitsPerDay?: number;
}

export interface ForecastSimulationRequest {
  inventoryId: string;
  country: string;
  whatIf: ForecastWhatIf;
  /** The same settings the page's /forecasts request used */
  startDate: string;
  endDate: string;
  [forecastSetting: string]: unknown;
}

/** Forecast groupings are the /forecasts row shape (untyped on this page) */
export interface ForecastSimulation {
  baseline: any;
  simulated: any;
  simulatedRiskItem: any | null;
}

/** Extra detail on a product-family row of /forecasts (the single item plus its multi-packs) */
export interface FamilyForecastInfo {
  baseInventoryId: string;
  baseInventoryName: string | null;
  /** What the family's numbers count, e.g. "units of 1-Pack / … Pillow" */
  unitLabel: string;
  members: Array<{
    key: string;
    inventoryId: string;
    inventoryName: string | null;
    /** Base units in one unit of this member (1 for the single item) */
    qty: number;
    stockOutDate: string | null;
    currentInventory: number;
    dailyRunRate: number;
  }>;
  /** Days between the first and last member to run out; null when one never does in range */
  stockOutSpreadDays: number | null;
  /** The house rule once the single item is out: single-item orders ship a whole pack */
  substitution: {
    baseStockOutDate: string | null;
    packsStockOutDate: string | null;
    substitutedOrders: number;
    giveawayUnits: number;
    packStockOutDates: Record<string, string | null>;
  };
}

/** Orders that shipped a different pack of a product than their variant usually does */
export interface PackSubstitutions {
  days: number;
  substitutions: Array<{
    baseInventoryId: string;
    country: string;
    orders: number;
    giveawayUnits: number;
    swaps: Array<{ usualItem: string; pickedItem: string; orders: number; units: number }>;
  }>;
}

export interface SalesRange {
  inventoryId: string;
  country: string;
  startDate: string;
  /** The last day counted: the requested end date, or yesterday when that is later */
  endDate: string;
  daysInRange: number;
  totalSales: number;
  dailyAverage: number;
}

function serverErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (data?.error && data.error !== GENERIC_SERVER_ERROR) return data.error;
  const reason = /^\s*(?:\w*Error): (.+)$/m.exec(String(data?.description || ""))?.[1];
  return reason || data?.error || fallback;
}

class InventoryService extends BaseService {
  async getRunRate(queryParams: Record<string, any> = {}) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/run-rate", { params: queryParams }),
      "Failed to fetch /run-rate",
    );
  }

  async getCurrentInventory(queryParams: Record<string, any> = {}) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/current-inventory", { params: queryParams }),
      "Failed to fetch /current-inventory",
    );
  }

  /**
   * One item's sales in one country over any date range (completed days only, so a range running
   * into today stops at yesterday). Throws with the server's message.
   */
  async getSalesRange(params: { inventoryId: string; country: string; startDate: string; endDate: string }): Promise<SalesRange> {
    await this._getAccessToken();
    try {
      const response = await this.http.get("inventory/sales-range", { params });
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to load sales for that date range"));
    }
  }

  /**
   * Inventory history must be fetched in slices (large payload).
   * Backend expects query params:
   * - startDate
   * - endDate
   * - inventoryIds (comma-separated string)
   *
   * NOTE: Internally we call them inventoryIds, but we must send inventoryIds to match the API contract.
   */
  async getInventoryHistory(params: { startDate: string; endDate: string; inventoryIds: string[] }) {
    await this._getAccessToken();

    const queryParams = {
      startDate: params.startDate,
      endDate: params.endDate,
      inventoryIds: params.inventoryIds.join(","), // API contract
    };

    return this.handleHttpRequest(
      () => this.http.get("inventory/inventory-history", { params: queryParams }),
      "Failed to fetch /inventory-history",
    );
  }

  /**
   * Save run rate modifiers for a specific inventory to the server.
   * @param data - { inventoryId, globalModifiers, inventoryModifiers, manualOverrides }
   */
  async saveRunRateModifiers(data: {
    inventoryId: string;
    globalModifiers?: Array<{ date: string; modifier: number }>;
    inventoryModifiers?: Array<{ date: string; modifier: number }>;
    manualOverrides?: Array<{ periodKey: string; value: number }>;
  }) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.put("inventory/run-rate-modifiers", data),
      "Failed to save /run-rate-modifiers",
    );
  }

  /**
   * Save ONE product's month multipliers and unit overrides (its complete sets: what is left out
   * is removed). Shared by everyone and used by the real forecast. Leaves the global multipliers
   * alone. Throws with the server's message.
   */
  async saveProductDemandSettings(inventoryId: string, settings: ProductDemandSettings): Promise<void> {
    await this._getAccessToken();
    try {
      await this.http.put("inventory/run-rate-modifiers", {
        inventoryId,
        inventoryModifiers: Object.entries(settings.salesModifiers).map(([monthKey, modifier]) => ({ date: `${monthKey}-01`, modifier })),
        manualOverrides: Object.entries(settings.manualOverrides).map(([periodKey, value]) => ({ periodKey, value })),
      });
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to save the adjustment"));
    }
  }

  /**
   * Re-forecast one product under unsaved demand settings. Saves nothing. `whatIf: {}` returns the
   * product's forecast as currently saved. Throws with the server's message.
   */
  async simulateForecast(request: ForecastSimulationRequest): Promise<ForecastSimulation> {
    await this._getAccessToken();
    try {
      const response = await this.http.post("inventory/forecasts/simulate", request);
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to simulate the forecast"));
    }
  }

  async getPackSubstitutions(days = 30): Promise<PackSubstitutions | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get("inventory/pack-substitutions", { params: { days } }),
      "Failed to fetch /pack-substitutions",
    );
  }

  /**
   * Fetch all incoming POs (no date filter).
   */
  async getIncomingPOs() {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/incoming-pos"),
      "Failed to fetch /incoming-pos",
    );
  }

  /**
   * Save/update incoming POs to the server.
   * @param pos - Array of PO objects to upsert
   */
  async saveIncomingPOs(pos: Array<{
    inventoryId: string;
    poNumber: string;
    arriveByDate: string;
    quantity: number;
    description: string;
    placementDate?: string | null;
    etd?: string | null;
    amount?: number | null;
    supplierId?: number | null;
    tariffRate?: number | null;
    status?: string;
    brand?: string | null;
    countryOfDestination?: string | null;
    paymentDateOverrides?: Record<string, string> | null;
    unitPrice?: number | null;
    priceType?: string | null;
    containerType?: string | null;
    manufacturerProductNumber?: string | null;
    shipToFcId?: number | null;
    skuConfirmed?: boolean;
  }>, isNewPO = false) {
    await this._getAccessToken();
    // Throws with the server's reason: a 400 here is something the person can act on (a PO
    // number already in use, a quantity edited below what ShipBob stowed).
    try {
      const response = await this.http.put("inventory/incoming-pos", { pos, isNewPO });
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || err?.response?.data?.description || "Failed to save the PO");
    }
  }

  /**
   * Rename a PO in place: its lines, NetSuite link, marks and WRO allocations move to the new
   * number. Throws with the server's reason (number already in use, PO gone).
   */
  async renamePO(from: string, to: string) {
    await this._getAccessToken();
    try {
      const response = await this.http.post("inventory/incoming-pos/rename", { from, to });
      return response.data;
    } catch (err: any) {
      const error: Error & { renamed?: boolean } = new Error(err?.response?.data?.error || `Failed to rename PO ${from} to ${to}`);
      // The server renamed the PO but a later step of the rename failed
      error.renamed = Boolean(err?.response?.data?.renamed);
      throw error;
    }
  }

  /**
   * Give the PO's NetSuite record the PO's current number (only the number changes).
   */
  async renameNetSuitePo(poNumber: string): Promise<{ netsuitePoId: string; tranId: string; previousTranId?: string; url: string }> {
    await this._getAccessToken();
    try {
      const response = await this.http.post(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/netsuite-rename`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || err?.response?.data?.message || "Failed to rename the PO in NetSuite");
    }
  }

  /**
   * Delete an incoming PO from the server.
   * @param inventoryId - The inventory ID
   * @param poNumber - The PO number to delete
   */
  async deleteIncomingPO(inventoryId: string, poNumber: string) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.delete("inventory/incoming-pos", { data: { inventoryId, poNumber } }),
      "Failed to delete /incoming-pos",
    );
  }

  /**
   * Mark confirmed POs as received.
   * @param poNumbers - Array of PO numbers to mark as received
   */
  async markPOsReceived(poNumbers: string[]) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/mark-received", { poNumbers }),
      "Failed to mark POs as received",
    );
  }

  /**
   * Unmark POs as received, reverting them to unreceived status.
   * @param poNumbers - Array of PO numbers to unmark
   */
  async unmarkPOsReceived(poNumbers: string[]) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/unmark-received", { poNumbers }),
      "Failed to unmark POs as received",
    );
  }

  /**
   * Update paid tranches for a single PO.
   * @param poNumber - The PO number
   * @param paidTranches - Object mapping tranche order to paid boolean, e.g. {"1": true, "2": false}
   */
  async updatePaidTranches(poNumber: string, paidTranches: Record<string, boolean> | null) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/update-paid-tranches", { poNumber, paidTranches }),
      "Failed to update paid tranches",
    );
  }

  /**
   * Clear all paid tranches for multiple POs (bulk unmark all).
   * @param poNumbers - Array of PO numbers
   */
  async clearPaidTranches(poNumbers: string[]) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/clear-paid-tranches", { poNumbers }),
      "Failed to clear paid tranches",
    );
  }

  /**
   * Promote a draft/forecasted PO to confirmed/placed status.
   * @param poNumber - The PO number to promote
   */
  async promotePOToConfirmed(poNumber: string) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/promote", { poNumber }),
      "Failed to promote PO to confirmed",
    );
  }

  /**
   * SKU costs: current unit cost per inventory item (pre-fills PO lines).
   */
  /**
   * The SKU master's current prices — the only unit-cost catalog. PO lines
   * pre-fill from it for the PO's container type.
   */
  async getCatalogPrices(): Promise<CatalogSuggestions | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/catalog-prices"),
      "Failed to fetch catalog prices",
    );
  }

  /**
   * ShipBob warehouse receiving orders (WROs) for the PO link picker.
   */
  async getWROs() {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/wros"),
      "Failed to fetch WROs",
    );
  }

  /**
   * ShipBob fulfillment centers that can receive inventory (WRO destinations).
   */
  async getReceivingFulfillmentCenters(): Promise<{
    fulfillmentCenters: Array<{
      id: number; name: string; address1: string | null; address2: string | null;
      city: string | null; state: string | null; zipCode: string | null; country: string | null;
      /** Set when we no longer ship to this center: the id of the one to use instead */
      replacedById?: number | null;
    }>;
  } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/receiving-fulfillment-centers"),
      "Failed to fetch receiving fulfillment centers",
    );
  }

  /**
   * Replace which WRO lines each line of a PO draws on (and how many units of each),
   * and whether the PO-number auto-match stays on. Returns the refreshed PO.
   */
  async setWroAllocations(poNumber: string, request: WroAllocationsRequest): Promise<{ pos: any[] }> {
    await this._getAccessToken();
    try {
      const response = await this.http.put(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/wro-allocations`, request);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.description || err?.response?.data?.error || "Failed to save the WRO allocations");
    }
  }

  /**
   * Replace every allocation on one WRO for every PO at once (the panel edits whole WRO
   * lines from whichever PO is open). Returns the WRO's lines and the refreshed POs touched.
   */
  async setWroLineAllocations(wroId: string, lines: WroLineAllocationsRequest["lines"]): Promise<{ wroId: string; lines: WroLineAllocations[]; pos: any[] }> {
    await this._getAccessToken();
    try {
      const response = await this.http.put(`inventory/wros/${encodeURIComponent(wroId)}/allocations`, { lines });
      return response.data;
    } catch (err: any) {
      const data = err?.response?.data;
      throw new Error(data?.message || data?.description || data?.error || `Failed to save the allocations on WRO ${wroId}`);
    }
  }

  /** Who is drawing on each line of a WRO: allocations by PO, and auto-matched POs. */
  async getWroAllocations(wroId: string): Promise<{ wroId: string; lines: WroLineAllocations[] }> {
    await this._getAccessToken();
    try {
      const response = await this.http.get(`inventory/wros/${encodeURIComponent(wroId)}/allocations`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || `Failed to load allocations for WRO ${wroId}`);
    }
  }

  /**
   * Pull a WRO created by hand in ShipBob into our mirror right now, so it can
   * be linked before the hourly sync sees it. Throws with the server's message.
   */
  async importWro(wroId: string): Promise<{ wro: WRO }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/wros/${encodeURIComponent(wroId)}/import`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || `Failed to import WRO ${wroId} from ShipBob`);
    }
  }

  /**
   * Re-send the PO (header, lines, ship-to) to its existing NetSuite record.
   * Throws with the server's message.
   */
  async updateNetSuitePo(poNumber: string): Promise<{ netsuitePoId: string; tranId: string; url: string }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/netsuite-update`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to update the PO in NetSuite");
    }
  }

  /**
   * Create a real warehouse receiving order in ShipBob for a PO. Throws with
   * the server's message so the dialog can show exactly why ShipBob refused.
   */
  async createWro(request: {
    poNumber: string;
    fulfillmentCenterId: number;
    packageType: "Package" | "Pallet" | "FloorLoadedContainer";
    expectedArrivalDate: string;
    /** What ShipBob shows as the WRO's PO number: the PO number plus an optional note */
    wroPoNumber?: string;
    /** Blank = the server sends ShipBob a placeholder; the container # is added in ShipBob later */
    trackingNumber?: string;
    items: Array<{ inventoryId: number; quantity: number }>;
  }): Promise<{ wro: { wroId: string; status: string | null; fulfillmentCenterName: string | null; purchaseOrderNumber?: string | null } }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post("inventory/wros", request);
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to create the WRO in ShipBob"));
    }
  }

  /**
   * Edit a WRO in place: quantities, arrival date, the PO string, the container number — only
   * what is passed. The WRO keeps its number and its box labels. Throws with the server's message.
   */
  async updateWro(wroId: string, request: {
    poNumber: string;
    items?: Array<{ inventoryId: number; quantity: number }>;
    expectedArrivalDate?: string;
    wroPoNumber?: string;
    trackingNumber?: string;
  }): Promise<{ wro: { wroId: string; fulfillmentCenterName: string | null } }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/wros/${wroId}/update`, request);
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to update the WRO in ShipBob"));
    }
  }

  /** Every short WRO, where its units went, its claims, and what ShipBob credited. Throws with the server's message. */
  async getMissingUnits(since?: string): Promise<MissingUnitsLedger> {
    await this._getAccessToken();
    try {
      const response = await this.http.get("inventory/missing-units", { params: since ? { since } : {} });
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to load the missing-units ledger"));
    }
  }

  /** The claims agent's drafted emails waiting for approval */
  async getClaimsOutbox(): Promise<{ emails: ClaimsOutboxEmail[] }> {
    await this._getAccessToken();
    try {
      const response = await this.http.get("inventory/claims-outbox");
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to load the emails waiting for approval"));
    }
  }

  /** Send one drafted email as edited; the server refuses one that is no longer due */
  async sendClaimsOutboxEmail(request: { key: string; to: string[]; cc: string[]; subject: string; body: string; approvedBy?: string }): Promise<void> {
    await this._getAccessToken();
    try {
      await this.http.post("inventory/claims-outbox/send", request);
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to send the email"));
    }
  }

  /** A short-lived link to a supplier document's PDF */
  async getSupplierDocumentUrl(id: number): Promise<{ url: string; fileName: string }> {
    await this._getAccessToken();
    try {
      const response = await this.http.get(`inventory/supplier-documents/${id}/pdf`);
      return response.data;
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to open the document"));
    }
  }

  /** Overrule the automatic split for one short line */
  async reviewShortfallLine(request: { wroId: string; inventoryId: string; category: ShortfallCategory; note?: string }): Promise<void> {
    await this._getAccessToken();
    try {
      await this.http.post("inventory/missing-units/review", request);
    } catch (err: any) {
      throw new Error(serverErrorMessage(err, "Failed to save the review"));
    }
  }

  /**
   * Completed WROs that received fewer units than expected, with credit math
   * and any claim already filed.
   */
  async getWroShortfalls(): Promise<{ shortfalls: WroShortfall[] } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/wro-shortfalls"),
      "Failed to fetch WRO shortfalls",
    );
  }

  /**
   * Acknowledge shortfalls without emailing — claim already filed by hand,
   * or the cause is known. One shared note; removes them from the flagged list.
   */
  async dismissWroShortfalls(request: {
    wros: Array<{ wroId: string; purchaseOrderNumber: string | null }>;
    note?: string;
  }): Promise<{ ok: boolean; dismissed: number }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post("inventory/wro-claims/dismiss", request);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to dismiss the shortfalls");
    }
  }

  /**
   * Move a claim through its lifecycle: info_requested, credited (amount
   * required), or denied. Throws with the server's message.
   */
  async updateWroClaim(claimId: number, updates: {
    status?: "sent" | "info_requested" | "credited" | "denied";
    creditedAmount?: number | null;
    note?: string;
    /** ShipBob's case number, from their auto-reply */
    caseNumber?: string | null;
  }): Promise<{ ok: boolean }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/wro-claims/${claimId}/update`, updates);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to update the claim");
    }
  }

  /**
   * Follow-up email on an open claim; resets its stale timer. Throws with the
   * server's message.
   */
  async sendWroClaimFollowUp(claimId: number, request: {
    to: string[];
    cc: string[];
    subject: string;
    body: string;
  }): Promise<{ ok: boolean }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/wro-claims/${claimId}/follow-up`, request);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to send the follow-up");
    }
  }

  /**
   * Email a missing-unit credit claim to ShipBob (invoice + packing list
   * attached) and record it. Throws with the server's message.
   */
  async sendWroClaim(request: {
    wroId: string;
    purchaseOrderNumber: string | null;
    to: string[];
    cc: string[];
    subject: string;
    body: string;
    lines: WroShortfallLine[];
    files: File[];
  }): Promise<{ ok: boolean; creditTotal: number | null }> {
    await this._getAccessToken();

    const form = new FormData();
    form.append("wroId", request.wroId);
    if (request.purchaseOrderNumber) form.append("purchaseOrderNumber", request.purchaseOrderNumber);
    form.append("to", JSON.stringify(request.to));
    form.append("cc", JSON.stringify(request.cc));
    form.append("subject", request.subject);
    form.append("body", request.body);
    form.append("lines", JSON.stringify(request.lines));
    for (const file of request.files) form.append("files", file);

    try {
      const response = await this.http.post("inventory/wro-claims", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to send the claim");
    }
  }

  /**
   * Find existing NetSuite POs and compare tool-linked orders. Read-only.
   */
  async getNetSuiteDrift(): Promise<NetSuiteDrift> {
    await this._getAccessToken();
    try {
      const response = await this.http.get("inventory/incoming-pos/netsuite-drift");
      return response.data.drift;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to compare POs with NetSuite");
    }
  }

  /**
   * Read the line items off a PO PDF and match them to SKUs. Nothing is saved;
   * the editor applies the result.
   */
  async extractPoPdfLines(poNumber: string, file: File): Promise<PoPdfExtraction> {
    await this._getAccessToken();
    const form = new FormData();
    form.append("file", file);
    try {
      const response = await this.http.post(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/pdf-lines`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      return response.data;
    } catch (err: any) {
      // Keep the server's own explanation: a 500 carries the real error in
      // `description`, and a network-level failure has only a status/message.
      const data = err?.response?.data;
      const detail = [data?.error, data?.description].filter(Boolean).join(" — ");
      const status = err?.response?.status ? ` (HTTP ${err.response.status})` : "";
      throw new Error(`Failed to read the PO PDF${status}: ${detail || err?.message || "no response from the server"}`);
    }
  }

  /**
   * What NetSuite would receive for this PO, with blockers named. Read-only.
   */
  async previewNetSuitePo(poNumber: string): Promise<NetSuitePoPreview> {
    await this._getAccessToken();

    try {
      const response = await this.http.get(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/netsuite-preview`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to build the NetSuite preview");
    }
  }

  /**
   * Create the PO in NetSuite (the confirmed step after the preview). Throws
   * with the server's message, which names the blocker when refused.
   */
  async pushNetSuitePo(poNumber: string): Promise<{ netsuitePoId: string; tranId: string; url: string }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post(`inventory/incoming-pos/${encodeURIComponent(poNumber)}/netsuite-push`);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to push the PO to NetSuite");
    }
  }

  /**
   * Email the PO PDF (last saved version) to the supplier. Throws with the
   * server's message on failure.
   */
  async emailPoPdf(request: {
    poNumber: string;
    to: string[];
    cc: string[];
    subject: string;
    body: string;
  }): Promise<{ ok: boolean }> {
    await this._getAccessToken();

    try {
      const response = await this.http.post("inventory/incoming-pos/email-pdf", request);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to send the PO email");
    }
  }

  /**
   * Create a new product in ShipBob and record its unit cost.
   */
  async createProduct(product: {
    name: string;
    sku: string;
    unitPrice: number;
    priceType: string;
    lengthIn?: number | null;
    widthIn?: number | null;
    heightIn?: number | null;
    weightOz?: number | null;
    countryOfOrigin?: string | null;
  }): Promise<{
    productId: string;
    inventoryId: string;
    sku: string;
    name: string;
    /** False when the product was created in ShipBob but its cost could not be stored */
    costRecorded: boolean;
    costError: string | null;
  } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("inventory/products", product),
      "Failed to create product",
    );
  }

  /**
   * Suggest the next PO number, continuing the existing series (e.g. "282 US").
   */
  async getNextPoNumber(country?: string | null): Promise<{ poNumber: string } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/next-po-number", { params: country ? { country } : {} }),
      "Failed to fetch /next-po-number",
    );
  }

  /**
   * Generate the PO document PDF; returns the PDF bytes for direct download.
   */
  async downloadPoPdf(poNumber: string): Promise<Blob | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("inventory/incoming-pos/pdf", { poNumber }, { responseType: "blob" }),
      "Failed to generate PO PDF",
    );
  }

  /**
   * Pin a PO to a ShipBob WRO (wroId null = pin to none, auto-match off), or
   * clear the pin with source 'auto' so the PO-number match applies again.
   */
  async linkPOToWRO(poNumber: string, link: WroLinkRequest) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.patch("inventory/incoming-pos/link-wro", { poNumber, ...link }),
      "Failed to link PO to WRO",
    );
  }

  async getForecasts(queryParams: Record<string, any> = {}) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/forecasts", { params: queryParams }),
      "Failed to fetch /forecasts",
    );
  }

  async getOosAlertIgnores(): Promise<{ ignores: Array<{ inventoryId: string; country: string }> } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/oos-alert-ignores"),
      "Failed to fetch /oos-alert-ignores",
    );
  }

  async addOosAlertIgnore(inventoryId: string, country: string) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("inventory/oos-alert-ignores", { inventoryId, country }),
      "Failed to add OOS alert ignore",
    );
  }

  async removeOosAlertIgnore(inventoryId: string, country: string) {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.delete("inventory/oos-alert-ignores", { data: { inventoryId, country } }),
      "Failed to remove OOS alert ignore",
    );
  }

  /**
   * Get lead times: manufacturer-level defaults plus per-inventory overrides (months).
   */
  async getLeadTimes(): Promise<LeadTimesResponse | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("inventory/lead-times"),
      "Failed to fetch /lead-times",
    );
  }

  /**
   * Upsert lead times. Either list may be omitted.
   */
  async saveLeadTimes(data: {
    manufacturerLeadTimes?: Array<{ manufacturer: string; leadTimeMonths: number }>;
    inventoryLeadTimes?: Array<{ inventoryId: string; leadTimeMonths: number }>;
    countryAdjustments?: Array<{ country: string; adjustmentMonths: number }>;
  }): Promise<LeadTimesResponse | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.put("inventory/lead-times", data),
      "Failed to save /lead-times",
    );
  }

  /**
   * Delete a manufacturer default, an inventory override, or a country adjustment.
   */
  async deleteLeadTime(target: { manufacturer?: string; inventoryId?: string; country?: string }): Promise<LeadTimesResponse | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.delete("inventory/lead-times", { data: target }),
      "Failed to delete /lead-times",
    );
  }
}

export default InventoryService;
